import { useMemo } from "react";
import { graphql } from "@repo/graphql-types/gql";
import { createFileRoute, useParams, useRouteContext } from "@tanstack/react-router";
import { z } from "zod";
import { Left } from "@repo/icons";
import { getCurrentDate } from "@repo/lib";
import { useGraphQL } from "@/hooks/use-graphql";
import { HeaderButton, HeaderRoot, HeaderTitle } from "@/components/header";
import { Page } from "@/components/page";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { PatientDetailHeader } from "@/components/patient-detail/patient-detail-header";
import { PatientDetailHeaderActions } from "@/components/patient-detail/patient-detail-header-actions";
import { PatientDetailButtons } from "@/components/patient-detail/patient-detail-buttons";
import { PatientDetailReminderAlert } from "@/components/patient-detail/patient-detail-reminder-alert";
import { PatientDetailTabs } from "@/components/patient-detail/patient-detail-tabs";

const PatientDetailPageQuery = graphql(/* GraphQL */ `
  query PatientDetailPageQuery($patientId: Int!, $today: datetime2!) {
    patient: pacientes_by_pk(codPaciente: $patientId) {
      ...PatientDetailActionListFragment
      ...PatientDetailPropertyListFragment
      ...PatientHistoryListFragment

      # New patient details layout fragments
      ...PatientDetailHeaderFragment
      ...PatientDetailHeaderActionsFragment
      ...PatientDetailLastAppointmentFragment
      ...PatientDetailTabsFragment
    }
  }
`);

const patientSchema = z.object({
  action: z.string().optional(),
});

export const PatientDetailPage = (): JSX.Element => {
  const { patientId } = useParams({ from: "/patients/$patientId/" });
  const queryResult = useGraphQL(PatientDetailPageQuery, {
    patientId: Number.parseInt(patientId),
    today: getCurrentDate().toISOString(),
  });
  const { data } = queryResult;

  const { flags } = useRouteContext({ strict: false });
  const showNewPatientLayout = useMemo(
    () => flags["exibe-nova-tela-de-paciente"],
    [flags],
  );

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Pacientes" />
      </HeaderRoot>
      <Page className="relative flex flex-col h-full">
        <AsyncDataWrapper {...queryResult}>
          {data?.patient ? (
            <>
              {showNewPatientLayout ? (
                <div className="mt-4 flex flex-col gap-6">
                  <PatientDetailHeader data={data.patient}>
                    <PatientDetailHeaderActions data={data.patient} />
                  </PatientDetailHeader>

                  <PatientDetailButtons data={data.patient} />
                  <PatientDetailReminderAlert data={data.patient} />

                  <PatientDetailTabs data={data.patient} />
                </div>
              ) : null}

              {!showNewPatientLayout && (
                <div className="mt-4 flex flex-col gap-6">
                  <PatientDetailHeader data={data.patient}>
                    <PatientDetailHeaderActions data={data.patient} />
                  </PatientDetailHeader>

                  <PatientDetailButtons data={data.patient} />
                  <PatientDetailReminderAlert data={data.patient} />

                  <PatientDetailTabs data={data.patient} />
                </div>
              )}
            </>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/patients/$patientId/")({
  component: PatientDetailPage,
  validateSearch: patientSchema,
});
